import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * Custom hook for scroll-triggered animations
 * Uses a callback ref to handle conditionally rendered elements correctly
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Intersection threshold (0-1)
 * @param {string} options.rootMargin - Root margin for intersection observer
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @returns {Array} [ref, isVisible]
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '50px',
    triggerOnce = true,
  } = options

  const [isVisible, setIsVisible] = useState(false)
  const observerRef = useRef(null)
  
  // Clean up observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  const ref = useCallback((node) => {
    if (node) {
      // Element mounted or updated
      
      // Clean up previous observer if exists
      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      // Check initial visibility immediately
      const checkInitialVisibility = () => {
        try {
          const rect = node.getBoundingClientRect()
          const windowHeight = window.innerHeight || document.documentElement.clientHeight
          
          // Simple parsing for rootMargin to estimate margin in pixels
          let marginValue = 50
          if (typeof rootMargin === 'string') {
             const match = rootMargin.match(/(-?\d+)px/)
             if (match) marginValue = parseInt(match[1])
          }

          // Check if element is in view or above view (scrolled past)
          // We add marginValue to extend the "visible" area
          const isVisible = (
            rect.top < (windowHeight + marginValue) &&
            rect.bottom > -marginValue
          )
          
          if (isVisible) {
            setIsVisible(true)
            return true
          }
        } catch (e) {
          console.error('Visibility check error:', e)
          // Fallback to visible on error to prevent hidden content
          setIsVisible(true)
          return true
        }
        return false
      }

      const wasVisible = checkInitialVisibility()
      if (wasVisible && triggerOnce) return

      // Create new observer
      try {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible(true)
              if (triggerOnce) {
                observer.unobserve(node)
                observer.disconnect()
              }
            } else if (!triggerOnce) {
              setIsVisible(false)
            }
          },
          { threshold, rootMargin }
        )
        
        observer.observe(node)
        observerRef.current = observer
      } catch (e) {
        // Fallback if IntersectionObserver fails or is not supported
        console.warn('IntersectionObserver failed, falling back to visible', e)
        setIsVisible(true)
      }
    } else {
      // Node unmounted
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [threshold, rootMargin, triggerOnce])

  return [ref, isVisible]
}
